// CRM 2.0 registration appointment on the customer's Agreement card (M13-F4).
// Shown only for CRM 2.0 bookings; old bookings keep the Anulom slot flow.
// The customer books on "Only customer" / "CRM and customer" projects; on "Only CRM"
// projects they see the appointment the CRM booked. Changing a date is the CRM's job.
import React, { useCallback, useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../Icons/Close";
import Button from "../../Subcomponents/Button";
import { errorTost, successTost } from "../../Services/Toast";
import { getCrm2Appointment, getCrm2Availability, bookCrm2Appointment } from "../../Services/Crm2Appointment";
import cssClasses from "./Crm2PortalAppointment.module.css";

const WEEKDAYS = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HALF_LABEL = { 1: "1st half", 2: "2nd half" };
const STATUS_TEXT = { AVAILABLE: "left", FEW: "left", FULL: "Full", CLOSED: "Closed" }; // colour shows "few"

const niceDate = (key) =>
	new Date(`${key}T00:00:00Z`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const halfText = (office, half) => {
	const hours = half === 1 ? office?.firstHalfHours : office?.secondHalfHours;
	return hours ? `${HALF_LABEL[half]} (${hours})` : HALF_LABEL[half];
};

const primaryBtn = { color: "#fff", backgroundColor: "rgb(21, 112, 239)", fontSize: "1.4rem", lineHeight: "2rem", fontFamily: "Inter-Medium !important" };
const secondaryBtn = { color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem", fontFamily: "Inter-Medium !important" };

const Crm2PortalAppointment = ({ allotmentPaymentId }) => {
	const [info, setInfo] = useState(null);
	const [failed, setFailed] = useState(false);
	const [bookOpen, setBookOpen] = useState(false);

	const load = useCallback(() => {
		setFailed(false);
		getCrm2Appointment(allotmentPaymentId)
			.then(setInfo)
			.catch(() => setFailed(true));
	}, [allotmentPaymentId]);

	useEffect(() => {
		if (allotmentPaymentId) load();
	}, [allotmentPaymentId, load]);

	if (failed) {
		return (
			<div className={cssClasses.section}>
				<div className={cssClasses.title}>Registration appointment</div>
				<p className={cssClasses.hint}>
					Could not load your appointment.{" "}
					<span className={cssClasses.link} onClick={load}>
						Try again
					</span>
				</p>
			</div>
		);
	}
	if (!info || !info.isCrm2) return null;

	const appt = info.appointment;
	const booked = info.appointmentBooked && appt;

	return (
		<div className={cssClasses.section}>
			<div className={cssClasses.head}>
				<div className={cssClasses.title}>Registration appointment</div>
				{booked && <span className={`${cssClasses.chip} ${cssClasses.chipOk}`}>Booked</span>}
				{appt?.status === "DONE" && <span className={`${cssClasses.chip} ${cssClasses.chipOk}`}>Registration done</span>}
				{!appt && !info.open && <span className={cssClasses.chip}>Not open yet</span>}
			</div>

			{(booked || appt?.status === "DONE") && (
				<div className={cssClasses.apptBox}>
					<div>
						<b>{niceDate(appt.date)}</b>, {halfText(appt.office, appt.half)}
					</div>
					{appt.office && (
						<>
							<div>
								Sub-Registrar office: <b>{appt.office.officeName}</b>
							</div>
							{appt.office.address && <div>{appt.office.address}</div>}
							{appt.office.mapUrl && (
								<a href={appt.office.mapUrl} target="_blank" rel="noreferrer">
									Open in Google Maps
								</a>
							)}
						</>
					)}
					<div className={cssClasses.hint}>{appt.bookedByType === "CUSTOMER" ? "Booked by you" : "Booked by your CRM"}</div>
				</div>
			)}

			{booked && (
				<>
					{info.whatToBring && (
						<p className={cssClasses.hint}>
							<b>What to bring:</b> {info.whatToBring}
						</p>
					)}
					<p className={cssClasses.hint}>All applicants must attend. To change the date, please call your CRM.</p>
				</>
			)}

			{!appt && !info.open && <p className={cssClasses.hint}>{info.notOpenReason}</p>}

			{!booked && appt?.status !== "DONE" && info.open && !info.customerMayBook && (
				<p className={cssClasses.hint}>Your CRM will book your registration appointment and let you know the date.</p>
			)}

			{!booked && appt?.status !== "DONE" && info.open && info.customerMayBook && (
				<>
					<p className={cssClasses.hint}>Pick a date at the Sub-Registrar office for registering your agreement.</p>
					<div className={cssClasses.actions}>
						<Button btnName="Book appointment" clicked={() => setBookOpen(true)} style={{ ...primaryBtn, width: "auto" }} />
					</div>
				</>
			)}

			{bookOpen && (
				<BookModal
					info={info}
					onClose={() => setBookOpen(false)}
					onBooked={(next) => {
						setBookOpen(false);
						if (next) setInfo(next);
						else load();
					}}
				/>
			)}
		</div>
	);
};

function BookModal({ info, onClose, onBooked }) {
	const [officeId, setOfficeId] = useState(info.offices.length === 1 ? info.offices[0].officeId : null);
	const [pages, setPages] = useState([null]); // the `from` of each page visited, for Back
	const [cal, setCal] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [pick, setPick] = useState(null); // { date, half }
	const [saving, setSaving] = useState(false);

	const from = pages[pages.length - 1];

	useEffect(() => {
		if (!officeId) return;
		let alive = true;
		setLoading(true);
		setError(null);
		getCrm2Availability(info.allotmentPaymentId, officeId, from)
			.then((d) => alive && setCal(d))
			.catch((e) => alive && setError(e.message))
			.finally(() => alive && setLoading(false));
		return () => {
			alive = false;
		};
	}, [info.allotmentPaymentId, officeId, from]);

	const chooseOffice = (id) => {
		if (id === officeId) return;
		setOfficeId(id);
		setPages([null]);
		setCal(null);
		setPick(null);
	};

	const confirm = () => {
		if (!pick || saving) return;
		setSaving(true);
		bookCrm2Appointment({ apId: info.allotmentPaymentId, officeId, date: pick.date, half: pick.half })
			.then((d) => {
				successTost(d?.emailed ? "Appointment booked. We have emailed you the details." : "Appointment booked.");
				onBooked(d?.appointment);
			})
			.catch((e) => {
				errorTost(e.message);
				setSaving(false);
				// The half may have just filled: reload this page of the calendar.
				setPick(null);
				setPages((p) => [...p]);
			});
	};

	const office = cal?.office || info.offices.find((o) => o.officeId === officeId);

	return (
		<Modal
			open
			onClose={(_, reason) => reason !== "backdropClick" && !saving && onClose()}
			sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }}
			disableAutoFocus
		>
			<Box className={cssClasses.modal}>
				<div className={cssClasses.modalHeader}>
					<span>Book registration appointment</span>
					<span className={cssClasses.modalClose} onClick={() => !saving && onClose()} tabIndex={0}>
						<Close width={"1.2rem"} height={"1.2rem"} />
					</span>
				</div>

				<div className={cssClasses.modalContent}>
					<div className={cssClasses.label}>1. Sub-Registrar office</div>
					<div className={cssClasses.officeCards}>
						{info.offices.map((o) => (
							<button
								type="button"
								key={o.officeId}
								className={`${cssClasses.officeCard} ${o.officeId === officeId ? cssClasses.officeCardOn : ""}`}
								onClick={() => chooseOffice(o.officeId)}
							>
								<b>{o.officeName}</b>
								{o.address && <span>{o.address}</span>}
								{o.mapUrl && (
									<a href={o.mapUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
										Open in Google Maps
									</a>
								)}
							</button>
						))}
					</div>

					{officeId && (
						<>
							<div className={cssClasses.label}>2. Date and half</div>
							<div className={cssClasses.legend}>
								<span><i className={cssClasses.dotAvail} /> Places left</span>
								<span><i className={cssClasses.dotFew} /> Few left</span>
								<span><i className={cssClasses.dotFull} /> Full</span>
								<span><i className={cssClasses.dotClosed} /> Closed</span>
							</div>
							{error && <p className={cssClasses.error}>{error}</p>}
							{loading && !cal && <p className={cssClasses.hint}>Loading dates…</p>}
							{cal && (
								<>
									<div className={cssClasses.dayGrid} style={{ opacity: loading ? 0.5 : 1 }}>
										{cal.days.map((d) => (
											<div key={d.date} className={`${cssClasses.day} ${d.closedReason ? cssClasses.dayClosed : ""}`}>
												<div className={cssClasses.dayName}>{niceDate(d.date)}</div>
												{d.closedReason ? (
													<div className={cssClasses.closedText}>{d.closedReason}</div>
												) : (
													d.halves.map((h) => {
														const on = pick?.date === d.date && pick?.half === h.half;
														const can = h.status === "AVAILABLE" || h.status === "FEW";
														return (
															<button
																type="button"
																key={h.half}
																disabled={!can}
																className={`${cssClasses.halfBtn} ${cssClasses[`half${h.status}`]} ${on ? cssClasses.halfOn : ""}`}
																onClick={() => can && setPick({ date: d.date, half: h.half })}
															>
																<span>{HALF_LABEL[h.half]}</span>
																<span>{can ? `${h.left} ${STATUS_TEXT[h.status]}` : STATUS_TEXT[h.status]}</span>
															</button>
														);
													})
												)}
											</div>
										))}
									</div>
									<div className={cssClasses.pager}>
										<Button
											btnName="Earlier dates"
											disable={pages.length === 1 || loading}
											clicked={() => pages.length > 1 && !loading && setPages((p) => p.slice(0, -1))}
											style={{ ...secondaryBtn, width: "auto" }}
										/>
										<Button
											btnName="Later dates"
											disable={!cal.nextFrom || loading}
											clicked={() => cal.nextFrom && !loading && setPages((p) => [...p, cal.nextFrom])}
											style={{ ...secondaryBtn, width: "auto" }}
										/>
									</div>
								</>
							)}
						</>
					)}

					{pick && (
						<p className={cssClasses.summary}>
							{office?.officeName}: <b>{niceDate(pick.date)}</b>, {halfText(office, pick.half)}
						</p>
					)}
					{info.attendees?.length > 0 && (
						<p className={cssClasses.hint}>
							All applicants must attend: <b>{info.attendees.join(", ")}</b>
						</p>
					)}
					{info.whatToBring && (
						<p className={cssClasses.hint}>
							<b>What to bring:</b> {info.whatToBring}
						</p>
					)}
					<p className={cssClasses.hint}>Once booked, only your CRM can change the date.</p>
				</div>

				<div className={cssClasses.modalFooter}>
					<Button btnName="Cancel" clicked={() => !saving && onClose()} style={{ ...secondaryBtn, width: "auto" }} />
					<Button btnName="Confirm booking" disable={!pick || saving} isLoading={saving} clicked={confirm} style={{ ...primaryBtn, width: "auto" }} />
				</div>
			</Box>
		</Modal>
	);
}

export default Crm2PortalAppointment;
